import {
	type ArchiveFilter,
	type ArchiveItem,
	type DateKey,
	type FilterKey,
	findStack,
	formatDuration,
	matchFilter,
	sortItems,
	isDateKey,
	parseQuery,
	type SortKey,
	type StackDef,
	weekday,
} from '@arrow/game-engine';

import { dailyPath, type PlayContext, practicePath } from '../../app/play-context.ts';
import { LEVEL_NAME } from '../today-model.ts';

/** Play's words: how an item, a filter and a sort read (en-GB, sentence case). */

export const SORT_NAME: Record<SortKey, string> = { new: 'Newest first', old: 'Oldest first', quick: 'Smallest first', big: 'Biggest first' };

const WEEKDAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "Sat 17 Oct". */
export function shortDate(key: DateKey): string {
	const [, m, d] = key.split('-').map(Number);
	return m && d && isDateKey(key) ? `${WEEKDAY_SHORT[weekday(key)]} ${d} ${MONTH_SHORT[m - 1]}` : key;
}

/** "Saturday 17 October 2026". */
export function longDate(key: DateKey): string {
	const [y, m, d] = key.split('-').map(Number);
	if (!y || !m || !d || !isDateKey(key)) return key;
	return `${WEEKDAY_LONG[weekday(key)]} ${d} ${MONTH_LONG[m - 1]} ${y}`;
}

/** "17 October" (a date someone typed, said back to them). */
export function dayMonthWords(key: DateKey): string {
	const [, m, d] = key.split('-').map(Number);
	return m && d ? `${d} ${MONTH_LONG[m - 1]}` : key;
}

export const plural = (n: number, one: string, many = `${one}s`) => (n === 1 ? one : many);

/** "No. 17 · Sat 17 Oct", or a practice puzzle's title. */
export function itemTitle(item: ArchiveItem): string {
	return item.kind === 'daily' && item.date ? `No. ${item.number} · ${shortDate(item.date)}` : (item.title ?? 'Practice');
}

/** "Hard · 10×10". */
export function itemMeta(item: ArchiveItem): string {
	return `${LEVEL_NAME[item.level]} · ${item.width}×${item.height}`;
}

/** "Fri’s Hard" (what "Play the top one" says), or a practice puzzle's title. */
export function itemName(item: ArchiveItem): string {
	return item.kind === 'daily' && item.weekday !== null ? `${WEEKDAY_SHORT[item.weekday]}’s ${LEVEL_NAME[item.level]}` : (item.title ?? 'Practice');
}

/** The state in words (for a screen reader, beside the glyph). */
export function stateWords(item: ArchiveItem): string {
	if (item.status === 'done') {
		const late = item.onTime === false ? ', caught up later' : '';
		return `${item.perfect ? 'perfect, ' : ''}solved in ${formatDuration(item.elapsedMs)}${late}`;
	}
	if (item.status === 'started') {
		const pct = item.pct !== null ? `${Math.round(item.pct * 100)} percent, ` : '';
		return `in progress, ${pct}${formatDuration(item.elapsedMs)} so far${item.missed ? ', missed on the day' : ''}`;
	}
	return item.missed ? 'missed, not started' : 'not started';
}

/** Everything about an item, in words: a row's accessible name. */
export function itemLabel(item: ArchiveItem): string {
	const where = item.kind === 'daily' && item.date ? `No. ${item.number}, ${longDate(item.date)}` : `${item.title ?? 'Practice'}, practice`;
	return `${where}, ${LEVEL_NAME[item.level]}, ${item.width} by ${item.height}, ${stateWords(item)}`;
}

const SIZE_NAME = { small: 'Up to 8×8', medium: '9×9 and 10×10', big: '11×11 and up' } as const;
const STATUS_NAME = { new: 'Not started', started: 'In progress', done: 'Done', perfect: 'Perfect', missed: 'Missed' } as const;
/** A status as a word before "puzzles": "No half-finished Hard puzzles." */
const STATUS_ADJ = { new: 'unstarted', started: 'half-finished', done: 'finished', perfect: 'perfect', missed: 'missed' } as const;
const SIZE_PHRASE = { small: 'on boards up to 8×8', medium: 'on 9×9 and 10×10 boards', big: 'on boards 11×11 and up' } as const;
const RANGE_NAME = { '7': 'Last 7 days', '30': 'Last 30 days', '90': 'Last 90 days' } as const;

export interface Token {
	key: FilterKey;
	/** The rule's kind ("Level"), quiet before the value. */
	group: string;
	label: string;
}

/** A filter's rules as chips: "Level Hard", "Status Missed", "Day Sundays"… */
export function filterTokens(f: ArchiveFilter): Token[] {
	const t: Token[] = [];
	for (const l of f.levels) t.push({ key: 'levels', group: 'Level', label: LEVEL_NAME[l] });
	for (const s of f.status) t.push({ key: 'status', group: 'Status', label: STATUS_NAME[s] });
	if (f.size !== 'any') t.push({ key: 'size', group: 'Size', label: SIZE_NAME[f.size] });
	if (f.weekday !== null) t.push({ key: 'weekday', group: 'Day', label: `${WEEKDAY_LONG[f.weekday]}s` });
	if (f.month) t.push({ key: 'month', group: 'When', label: `${MONTH_LONG[f.month.month - 1]} ${f.month.year}` });
	if (f.range !== 'all') t.push({ key: 'range', group: 'When', label: RANGE_NAME[f.range] });
	return t;
}

/** "missed Hard Sunday puzzles in October 2026": what a search asked for, said back. */
export function describeFilter(f: ArchiveFilter): string {
	const bits: string[] = [];
	const both = f.status.length === 2 && f.status.includes('new') && f.status.includes('started');
	if (both) bits.push('unfinished');
	else if (f.status.length) bits.push(f.status.map((s) => STATUS_ADJ[s]).join(' or '));
	if (f.levels.length) bits.push(f.levels.map((l) => LEVEL_NAME[l]).join(' or '));
	if (f.weekday !== null) bits.push(WEEKDAY_LONG[f.weekday]!);
	bits.push('puzzles');
	if (f.size !== 'any') bits.push(SIZE_PHRASE[f.size]);
	if (f.month) bits.push(`in ${MONTH_LONG[f.month.month - 1]} ${f.month.year}`);
	if (f.range !== 'all') bits.push(`in the ${RANGE_NAME[f.range].toLowerCase()}`);
	return bits.join(' ');
}

/** A loosening's button: "Any level", "Any status", "Any size"… */
export function relaxLabel(key: FilterKey): string {
	switch (key) {
		case 'levels':
			return 'Any level';
		case 'status':
			return 'Any status';
		case 'size':
			return 'Any size';
		case 'range':
			return 'Any time';
		case 'weekday':
			return 'Any day';
		case 'month':
			return 'Any month';
	}
}

/** Where an item opens, carrying how it was reached. */
export function itemPath(item: ArchiveItem, ctx: PlayContext | null): string {
	if (item.kind === 'practice') return practicePath(item.puzzleId, null, ctx);
	return dailyPath(item.number!, item.level, ctx);
}

/**
 * The list a game opened from Play belongs to, in its order: the stack's, or the search's.
 * `keep` stays in it even if it no longer matches (the game just finished, so it's left
 * Missed), so "next" still means the one after it. Null for a lookup (one day) or no context.
 */
export function contextItems(
	ctx: PlayContext | null | undefined,
	dailies: readonly ArchiveItem[],
	practice: readonly ArchiveItem[],
	todayKey: DateKey,
	keep: string | null = null,
	mine: readonly StackDef[] = [],
): ArchiveItem[] | null {
	if (!ctx) return null;
	let source: readonly ArchiveItem[];
	let filter: ArchiveFilter;
	const stack = findStack(ctx.stack, mine);
	if (stack) {
		source = stack.source === 'practice' ? practice : dailies;
		filter = { ...stack.filter, sort: ctx.sort ?? stack.filter.sort };
	} else {
		const p = ctx.q ? parseQuery(ctx.q, todayKey) : null;
		if (!p || p.kind !== 'words') return null;
		source = dailies;
		filter = { ...p.filter, sort: ctx.sort ?? p.filter.sort };
	}
	return sortItems(
		source.filter((it) => it.key === keep || matchFilter(it, filter)),
		filter.sort,
	);
}

/** "Missed" / "your search": what the poster's way on is the next in. */
export function contextName(ctx: PlayContext | null | undefined, mine: readonly StackDef[] = []): string {
	const stack = findStack(ctx?.stack, mine);
	return stack ? stack.name : 'your search';
}
